import { useTranslation } from "react-i18next";
import {
  Sparkles, ShieldCheck, Activity, Layers,
  Zap, TrendingDown, Coins, BarChart3, Gem, Bitcoin,
  Wallet, Timer, BadgeCheck, Building2, Lock, FileCheck,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarketTicker from "@/components/MarketTicker";
import PageHero from "@/components/PageHero";
import LiveSpreadsPanel from "@/components/LiveSpreadsPanel";
import AccountTypesSection from "@/components/AccountTypesSection";
import OnboardingSteps from "@/components/OnboardingSteps";
import StatsCounters from "@/components/StatsCounters";
import FeaturedIn from "@/components/FeaturedIn";
import ScrollProgress from "@/components/ScrollProgress";
import HeroStatStrip from "@/components/HeroStatStrip";
import CTABanner from "@/components/CTABanner";
import FaqJsonLd from "@/components/FaqJsonLd";
import MT5PlatformShowcase from "@/components/MT5PlatformShowcase";
import LiquidityEcosystem from "@/components/LiquidityEcosystem";
import WhyOFinancialMarkets from "@/components/WhyOFinancialMarkets";
import PartnerBanner from "@/components/PartnerBanner";
import FAQ from "@/components/FAQ";
import { useCtaTarget } from "@/lib/useCtaTarget";

const GOLD = "#d4af37";

/* OFinancial Markets home · broker funnel:
   Hero → trust strip → trading assets → MT5 platform → onboarding →
   account types → liquidity tech → why us → partner banner →
   live spreads → stats → social proof → FAQ → CTA → footer. */
export default function HomePage() {
  const { t } = useTranslation();
  // Per-location CTA target for the slide-1 "Open Live Account" button.
  // Defaults to direct CRM (`isExternal=true`); admin can flip to the
  // in-app wizard at /admin/dashboard → Settings → CTA Routing → Hero.
  const heroCta = useCtaTarget("hero");

  /* Four cycling home messages. Each slide has its OWN 4-card floor
     that visually represents what it's saying — institutional pillars
     for the institution slide, deposit-bonus mechanics for the bonus
     slide, asset class breakdown for the markets slide, and licensing /
     security pillars for the regulated slide. The bonus slide is
     gold-themed (matches /first-deposit-bonus) so the CTA, headline
     gradient, pill chip, dot indicator and orb tint all shift together
     while the slide is active. */
  const slides = [
    {
      key: "institution",
      artwork: "institutional",
      pill:    { Icon: Activity,     text: t("hero.slide1.eyebrow", "Institutional · MT5 · Raw spreads") },
      title:   t("hero.slide1.head1", "Trade markets like"),
      accent:  t("hero.slide1.head2", "an institution"),
      subline: t("hero.slide1.sub",   "1200+ instruments. Raw spreads from 0.0 pips. Dynamic leverage up to 1:1000. One MT5 account · desktop, web, mobile."),
      primary:   { label: t("hero.slide1.cta1", "Open Live Account"), external: heroCta.isExternal, href: heroCta.href },
      secondary: { label: t("hero.slide1.cta2", "Compare accounts"), to: "/account-comparison" },
      features: [
        { Icon: ShieldCheck,   label: t("hero.features.ndd.label", "Direct Market Access"),  desc: t("hero.features.ndd.desc", "Orders route straight to tier-1 liquidity providers · no intermediation.") },
        { Icon: Zap,           label: t("hero.features.speed.label", "Lightning routing"),    desc: t("hero.features.speed.desc", "Sub-millisecond order placement on every account.") },
        { Icon: TrendingDown,  label: t("hero.features.rawSpreads.label", "Raw spreads"),    desc: t("hero.features.rawSpreads.desc", "From 0.0 pips on EURUSD · no markup, no hidden fees.") },
        { Icon: Layers,        label: t("hero.features.instruments.label", "1200+ symbols"), desc: t("hero.features.instruments.desc", "Forex, indices, metals, crypto, energies, shares.") },
      ],
    },
    {
      key: "bonus",
      artwork: "bonus",
      accentColor: GOLD,
      accentBg:    GOLD,
      accentBgDeep: "#b8941f",
      accentInk:   "#1a1106",
      pill:    { Icon: Sparkles,    text: t("hero.slide2.eyebrow", "Limited launch promotion") },
      title:   t("hero.slide2.head1", "50% First Deposit Bonus"),
      accent:  t("hero.slide2.head2", "up to $5,000"),
      subline: t("hero.slide2.sub",   "Open your first OFinancial Markets account and we'll match 50% of your first deposit · turn $1,000 into $1,500 of trading power on day one."),
      primary:   { label: t("hero.slide2.cta1", "Claim my bonus"), to: "/first-deposit-bonus" },
      secondary: { label: t("hero.slide2.cta2", "Read the conditions"), to: "/first-deposit-bonus#conditions" },
      features: [
        { Icon: Sparkles,    label: t("hero.slide2Features.f1.label", "50% deposit match"),  desc: t("hero.slide2Features.f1.desc", "Get $1.50 of trading capital for every $1 deposited on day one.") },
        { Icon: Wallet,      label: t("hero.slide2Features.f2.label", "Up to $5,000"),       desc: t("hero.slide2Features.f2.desc", "Maximum bonus credit per qualified account.") },
        { Icon: Timer,       label: t("hero.slide2Features.f3.label", "24-hour credit"),     desc: t("hero.slide2Features.f3.desc", "Bonus is posted to your account the same day.") },
        { Icon: BadgeCheck,  label: t("hero.slide2Features.f4.label", "Eligible accounts"),  desc: t("hero.slide2Features.f4.desc", "Any eligible live trading account.") },
      ],
    },
    {
      key: "markets",
      artwork: "markets",
      pill:    { Icon: Layers,      text: t("hero.slide3.eyebrow", "Multi-asset · one login") },
      title:   t("hero.slide3.head1", "1200+ instruments"),
      accent:  t("hero.slide3.head2", "one MT5 terminal"),
      subline: t("hero.slide3.sub",   "FX · metals · energies · indices · 50+ crypto pairs · global share CFDs. All from a single account."),
      primary:   { label: t("hero.slide3.cta1", "Browse symbols"), to: "/symbols" },
      secondary: { label: t("hero.slide3.cta2", "See live spreads"), to: "/live-spreads" },
      features: [
        { Icon: Coins,      label: t("hero.slide3Features.f1.label", "78+ FX pairs"),       desc: t("hero.slide3Features.f1.desc", "Majors, minors and exotics on raw-pricing feeds.") },
        { Icon: BarChart3,  label: t("hero.slide3Features.f2.label", "Global indices"),     desc: t("hero.slide3Features.f2.desc", "S&P 500, Nasdaq, DAX, FTSE, Nikkei and more.") },
        { Icon: Gem,        label: t("hero.slide3Features.f3.label", "Spot metals"),        desc: t("hero.slide3Features.f3.desc", "Gold, silver, platinum, palladium · per-troy-ounce.") },
        { Icon: Bitcoin,    label: t("hero.slide3Features.f4.label", "50+ crypto pairs"),   desc: t("hero.slide3Features.f4.desc", "BTC, ETH, SOL, XRP versus USD plus crypto-crypto.") },
      ],
    },
    {
      key: "regulated",
      artwork: "regulated",
      pill:    { Icon: ShieldCheck, text: t("hero.slide4.eyebrow", "Global coverage · Licensed") },
      title:   t("hero.slide4.head1", "Global coverage &"),
      accent:  t("hero.slide4.head2", "fully licensed"),
      subline: t("hero.slide4.sub",   "Our financial group is authorized and licensed in Antigua and Mauritius. Client funds held in segregated tier-1 bank accounts. Negative balance protection on every retail account · no client owes more than they deposit."),
      primary:   { label: t("hero.slide4.cta1", "Our regulation"), to: "/regulation" },
      secondary: { label: t("hero.slide4.cta2", "Safety of funds"), to: "/safety-of-funds" },
      features: [
        { Icon: ShieldCheck,  label: t("hero.slide4Features.f1.label", "Licensed group"),        desc: t("hero.slide4Features.f1.desc", "Authorized and licensed in Antigua and Mauritius.") },
        { Icon: Building2,    label: t("hero.slide4Features.f2.label", "Segregated funds"),      desc: t("hero.slide4Features.f2.desc", "Held in tier-1 bank trust accounts at all times.") },
        { Icon: Lock,         label: t("hero.slide4Features.f3.label", "Negative balance"),      desc: t("hero.slide4Features.f3.desc", "Protection on every retail account · loss capped.") },
        { Icon: FileCheck,    label: t("hero.slide4Features.f4.label", "Independently audited"), desc: t("hero.slide4Features.f4.desc", "Annual financial audit by a Big-4 affiliate.") },
      ],
    },
  ];

  return (
    <div data-testid="home-page" className="relative overflow-x-hidden">
      <FaqJsonLd />
      <ScrollProgress />
      <Navbar />

      <div id="hero" />
      <PageHero slides={slides} testId="home-hero" />

      <HeroStatStrip />

      {/* Real-time spreads filterable by asset class · trading-assets section */}
      <LiveSpreadsPanel />

      {/* "As seen on" · FSC Mauritius licence press coverage */}
      <FeaturedIn />

      {/* MT5 download / web terminal / mobile platforms */}
      <MT5PlatformShowcase />

      {/* "Start trading in 3 steps" · broker onboarding flow */}
      <OnboardingSteps />

      {/* Pick your account type · the buy decision */}
      <AccountTypesSection />

      {/* "Empowering trading with technology" · LD4 liquidity ecosystem */}
      <LiquidityEcosystem />

      {/* "Why OFinancial Markets" · 3-card differentiator section */}
      <WhyOFinancialMarkets />

      {/* "There has never been a better opportunity to refer traders" */}
      <PartnerBanner />

      <StatsCounters />

      {/* FAQ · broker-only content */}
      <FAQ />

      <MarketTicker />

      <CTABanner />
      <Footer />
    </div>
  );
}
